/** Dosen-PA (perwalian) consultations + PA-link requests. */

import { createDoc, updateDoc, getById, fetchQuery } from '../service';
import { COL, q, col, where } from '../../collections';
import { utcNowIso } from '../../format';
import type { KonsultasiPaDoc, MahasiswaDoc, PaRequestDoc } from '../../types';

/** Log a PA consultation (mahasiswa → Dosen PA). */
export async function addKonsultasiPa(data: {
  mahasiswaId: string;
  dosenPaId: string;
  tanggal?: string;
  topik: string;
  catatan?: string;
}) {
  return createDoc<KonsultasiPaDoc>(COL.konsultasiPa, {
    ...data,
    tanggal: data.tanggal ?? utcNowIso(),
    status: 'menunggu',
  });
}

export async function updateKonsultasiPaStatus(
  id: string,
  status: KonsultasiPaDoc['status'],
  feedback?: string,
) {
  return updateDoc<KonsultasiPaDoc>(COL.konsultasiPa, id, { status, feedback });
}

/**
 * Mahasiswa requests a dosen to become their Dosen PA. The link only becomes
 * active once the dosen approves via verifyDosenPa. Any earlier pending
 * request from this mahasiswa is automatically withdrawn.
 */
export async function requestDosenPa(input: {
  mahasiswaId: string;
  dosenId: string;
  requestedBy: string;
}) {
  const existing = await fetchQuery<PaRequestDoc>(
    q(
      col(COL.paRequests),
      where('mahasiswaId', '==', input.mahasiswaId),
      where('status', '==', 'pending'),
    ),
  );
  for (const ex of existing) {
    await updateDoc<PaRequestDoc>(COL.paRequests, ex.id, {
      status: 'rejected',
      decidedAt: utcNowIso(),
      note: 'Dibatalkan karena ada permintaan baru.',
    });
  }
  const now = utcNowIso();
  return createDoc<PaRequestDoc>(COL.paRequests, {
    mahasiswaId: input.mahasiswaId,
    dosenId: input.dosenId,
    status: 'pending',
    requestedBy: input.requestedBy,
    requestedAt: now,
    createdAt: now,
    updatedAt: now,
  });
}

/**
 * Dosen verifies a pending PA request. Approval is what actually links the
 * mahasiswa ↔ dosen (sets MahasiswaDoc.dosenPAId); rejection leaves the link
 * unset so the mahasiswa can request another dosen.
 */
export async function verifyDosenPa(input: {
  id: string;
  status: 'approved' | 'rejected';
  note?: string;
}) {
  const req = await getById<PaRequestDoc>(COL.paRequests, input.id);
  if (!req || req.status !== 'pending') return;
  if (input.status === 'approved') {
    const mhs = await fetchQuery<MahasiswaDoc>(
      q(col(COL.mahasiswa), where('userId', '==', req.mahasiswaId)),
    );
    if (mhs[0]) {
      await updateDoc<MahasiswaDoc>(COL.mahasiswa, mhs[0].id, {
        dosenPAId: req.dosenId,
        updatedAt: utcNowIso(),
      });
    }
  }
  return updateDoc<PaRequestDoc>(COL.paRequests, input.id, {
    status: input.status,
    note: input.note,
    decidedAt: utcNowIso(),
    updatedAt: utcNowIso(),
  });
}
